import { useMutation } from '@tanstack/react-query';
import { ediphorDiagnosticsService } from '@/services/api/ediphorDiagnosticsService';
import type { EdiphorTokenInfoDto } from '../types';

interface TokenStatusProps {
  tokenInfo?: EdiphorTokenInfoDto;
  onRefresh: () => void;
}

export function TokenStatus({ tokenInfo, onRefresh }: TokenStatusProps) {
  const invalidateMutation = useMutation({
    mutationFn: () => ediphorDiagnosticsService.invalidateTokens(),
    onSuccess: () => {
      alert('Tokens invalidated successfully');
      onRefresh();
    },
    onError: (error: any) => {
      alert(`Failed to invalidate tokens: ${error.message}`);
    }
  });

  const authenticateMutation = useMutation({
    mutationFn: () => ediphorDiagnosticsService.forceAuthenticate(),
    onSuccess: () => {
      alert('Authentication successful!');
      onRefresh();
    },
    onError: (error: any) => {
      alert(`Authentication failed: ${error.message}`);
    }
  });

  const formatExpiry = (expiresAt: string | null, secondsRemaining: number | null) => {
    if (!expiresAt || secondsRemaining === null) return 'N/A';
    const date = new Date(expiresAt);
    const minutes = Math.floor(secondsRemaining / 60);
    return `${date.toLocaleTimeString()} (${minutes}m remaining)`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-gray-700 dark:to-gray-600 border-b border-blue-200 dark:border-gray-600 px-6 py-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <span className="text-2xl">🔐</span>
          Authentication Status
        </h2>
      </div>

      {!tokenInfo ? (
        <div className="p-6 text-center">
          <div className="animate-spin inline-block w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full mb-2" />
          <p className="text-gray-600 dark:text-gray-400">Loading token status...</p>
        </div>
      ) : (
        <div className="p-6 space-y-6">
          {/* Status Summary */}
          <div className={`rounded-lg p-4 ${
            tokenInfo.accessToken.isValid && tokenInfo.refreshToken.isPresent
              ? 'bg-green-50 dark:bg-green-900 dark:bg-opacity-30 border border-green-200 dark:border-green-700'
              : 'bg-red-50 dark:bg-red-900 dark:bg-opacity-30 border border-red-200 dark:border-red-700'
          }`}>
            <div className="flex items-start gap-3">
              <span className={`text-2xl ${
                tokenInfo.accessToken.isValid && tokenInfo.refreshToken.isPresent ? '✅' : '⚠️'
              }`} />
              <div>
                <h3 className={`font-semibold ${
                  tokenInfo.accessToken.isValid && tokenInfo.refreshToken.isPresent
                    ? 'text-green-900 dark:text-green-200'
                    : 'text-red-900 dark:text-red-200'
                }`}>
                  {tokenInfo.accessToken.isValid && tokenInfo.refreshToken.isPresent
                    ? 'Authenticated & Ready'
                    : 'Authentication Required'}
                </h3>
                <p className={`text-sm ${
                  tokenInfo.accessToken.isValid && tokenInfo.refreshToken.isPresent
                    ? 'text-green-800 dark:text-green-300'
                    : 'text-red-800 dark:text-red-300'
                }`}>
                  {tokenInfo.accessToken.isValid && tokenInfo.refreshToken.isPresent
                    ? 'Your service is connected and ready to use'
                    : 'Click "Force Authenticate" to establish a connection'}
                </p>
              </div>
            </div>
          </div>

          {/* Token Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Access Token */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Access Token
                </h3>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  tokenInfo.accessToken.isValid
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                    : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${tokenInfo.accessToken.isValid ? 'bg-green-500' : 'bg-red-500'}`} />
                  {tokenInfo.accessToken.isValid ? 'Valid' : 'Invalid'}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-gray-600 dark:text-gray-400">Expires at</p>
                  <p className="font-mono text-gray-900 dark:text-white text-xs">
                    {formatExpiry(tokenInfo.accessToken.expiresAt, tokenInfo.accessToken.secondsRemaining)}
                  </p>
                </div>
                {tokenInfo.accessToken.tokenPreview && (
                  <div>
                    <p className="text-gray-600 dark:text-gray-400 text-xs">Token preview</p>
                    <p className="font-mono text-gray-700 dark:text-gray-300 text-xs truncate bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                      {tokenInfo.accessToken.tokenPreview}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Refresh Token */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Refresh Token
                </h3>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  tokenInfo.refreshToken.isPresent
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${tokenInfo.refreshToken.isPresent ? 'bg-green-500' : 'bg-gray-400'}`} />
                  {tokenInfo.refreshToken.isPresent ? 'Present' : 'Missing'}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <p className="text-gray-600 dark:text-gray-400">
                  {tokenInfo.refreshToken.isPresent
                    ? 'Refresh token is cached and ready'
                    : 'No refresh token available'}
                </p>
                {tokenInfo.refreshToken.tokenPreview && (
                  <div>
                    <p className="text-gray-600 dark:text-gray-400 text-xs">Token preview</p>
                    <p className="font-mono text-gray-700 dark:text-gray-300 text-xs truncate bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                      {tokenInfo.refreshToken.tokenPreview}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => authenticateMutation.mutate()}
              disabled={authenticateMutation.isPending}
              className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {authenticateMutation.isPending ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  Authenticating...
                </>
              ) : (
                <>
                  🔓 Force Authenticate
                </>
              )}
            </button>
            <button
              onClick={() => {
                if (confirm('This will clear all cached tokens. Continue?')) {
                  invalidateMutation.mutate();
                }
              }}
              disabled={invalidateMutation.isPending}
              className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {invalidateMutation.isPending ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  Clearing...
                </>
              ) : (
                <>
                  🗑️ Clear Tokens
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
